import React, { useContext, useEffect, useState } from "react";
import useGet from "../hooks/useGet";
import LazyLoadingContext from "./LazyLoadingContext";
import { isShyMainSite, SHY_MAIN_SITE_HOSTNAMES } from "../utilities/poweredBy";

const PoweredByContext = React.createContext({
    isPoweredBy: false,
});

export const PoweredByContextProvider = (props) => {
    const [domainsAndTenantsUrl, setDomainsAndTenantsUrl] = useState(null);
    const [tenantLookupUrl, setTenantLookupUrl] = useState(null);
    const [tenantID, setTenantID] = useState("");
    const [options, setOptions] = useState({
        isPoweredBy: "",
        siteReady: false,
        siteChecking: true,
    });
    const [tenantIdReady, setTenantIdReady] = useState(false);
    const [tenantConfigReady, setTenantConfigReady] = useState(false);

    const lazyLoadingCtx = useContext(LazyLoadingContext);

    const { returnData: domainsAndTenants } = useGet(domainsAndTenantsUrl);
    const { returnData: tenantConfig } = useGet(tenantLookupUrl);

    useEffect(() => {
        if (domainsAndTenants !== false && domainsAndTenants !== "") {
            lazyLoadingCtx.updateGlobalCheck("domainsAndTenants", true);
        }
    }, [domainsAndTenants, lazyLoadingCtx]);

    useEffect(() => {
        if (tenantConfig !== false && tenantConfig !== "") {
            lazyLoadingCtx.updateGlobalCheck("tenantConfig", true);
        }
    }, [tenantConfig, lazyLoadingCtx]);

    useEffect(() => {
        if (isShyMainSite()) {
            setOptions({
                ...options,
                siteReady: true,
                siteChecking: false,
            });
            lazyLoadingCtx.updateGlobalCheck("domainsAndTenants", true);
            lazyLoadingCtx.updateGlobalCheck("tenantConfig", true);
        } else {
            setOptions({ ...options });
            setDomainsAndTenantsUrl("/api/poweredbyshy/domains-and-tenants");
        }
        // We intentionally only run this initialisation once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // We intentionally only depend on domainsAndTenants here; getTenantID reads from the latest domainsAndTenants.
        if (domainsAndTenants.domains && domainsAndTenants.tenantIds) {
            setTenantID(getTenantID());
            setTenantIdReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainsAndTenants]);

    useEffect(() => {
        if (tenantID !== "") {
            setTenantLookupUrl(`/api/poweredbyshy/tenant-by-id?tenantId=${tenantID}`);
        }
    }, [tenantID]);

    // tenantID / applyTheme / getTenantUrl are intentionally omitted:
    // applyTheme and getTenantUrl are local closures, and adding `options`
    // would loop because we call setOptions inside. Functional update keeps
    // the read of `options` honest.
    useEffect(() => {
        if (tenantConfig !== "" && tenantConfig !== false) {
            setTenantConfigReady(true);
            setOptions((prev) => ({
                ...prev,
                ...tenantConfig,
                tenantID: tenantID,
                tenantUrl: getTenantUrl(tenantID),
                getTenantUrlFromId: getTenantUrl,
                domainsAndTenants: domainsAndTenants,
                applyTheme: applyTheme,
                isPoweredBy: tenantID !== "",
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantConfig, domainsAndTenants]);

    useEffect(() => {
        if (tenantIdReady && tenantID === "") {
            setOptions((prev) => ({
                ...prev,
                siteReady: true,
                siteChecking: false,
            }));
        } else if (tenantIdReady && tenantConfigReady && tenantID !== "") {
            setOptions((prev) => ({
                ...prev,
                siteReady: true,
                siteChecking: false,
            }));
        }
    }, [tenantIdReady, tenantConfigReady, tenantID]);

    const queryParams = new URLSearchParams(window.location.search);

    const getTenantID = () => {
        const hostname = window.location.hostname.split(".");
        let id = "";

        if (queryParams.get("tenantID") !== null) {
            // URL param tenant ID
            id = queryParams.get("tenantID");
        } else if (domainsAndTenants.domains[window.location.hostname] !== undefined) {
            // Domain tenant ID
            id = domainsAndTenants.domains[window.location.hostname];
        } else if (domainsAndTenants.domains[window.location.hostname.replace("www.", "")] !== undefined) {
            // Domain tenant ID
            id = domainsAndTenants.domains[window.location.hostname.replace("www.", "")];
        } else if (hostname.length > 1 && SHY_MAIN_SITE_HOSTNAMES.indexOf(window.location.hostname) === -1) {
            // Subdomain tenant ID
            id = hostname[0];
        }

        if (domainsAndTenants.tenantIds[id] !== undefined) {
            id = domainsAndTenants.tenantIds[id];
        }

        return id;
    };

    const getTenantUrl = (tenantID) => {
        if (!domainsAndTenants || !domainsAndTenants.domains || !domainsAndTenants.tenantIds) {
            return null;
        }

        let id;
        let url;

        Object.keys(domainsAndTenants.tenantIds).forEach((tenantId) => {
            if (tenantID === domainsAndTenants.tenantIds[tenantId]) {
                id = tenantId;
            }
        });

        Object.keys(domainsAndTenants.domains).forEach((domain) => {
            if (id === domainsAndTenants.domains[domain]) {
                url = domain;
            }
        });

        return url;
    };

    // Helper function to convert camelCase to kebab-case
    const camelToKebabCase = (str) => {
        return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    };

    // Apply the theme to the site
    const applyTheme = (theme) => {
        const root = document.querySelector(":root");

        document.querySelector("body").classList.add("is-powered-by");

        Object.keys(theme).forEach((item) => {
            const kebabCaseItem = camelToKebabCase(item);

            root.style.setProperty(`--${kebabCaseItem}`, theme[item]);
        });
    };

    return <PoweredByContext.Provider value={options}>{props.children}</PoweredByContext.Provider>;
};

export default PoweredByContext;
